import { useEffect, useMemo, useState } from 'react'
import {
  fetchLeadsByChannel, fetchStatsByBranch, fetchStatsSeries,
  type LeadChannelStat, type SeriesGroupBy, type StatsBranch, type StatsSeries,
} from '../api/stats'
import type { ChartRange } from './chartRange'
import { fetchCommissionsSeries, type CommissionsSeries } from '../api/commissions'
import { BarChart, Callout, ChartCard, DonutChart, LineChart, type ChartColor, type ChartSeries, type DonutSlice } from './ui'

/**
 * Grafici G3: i grafici di Dashboard, Funnel e Provvigioni. Ogni componente carica la propria serie
 * (GET /api/stats/series, by-branch, leads-by-channel, commissions/series) per l'intervallo `from`/`to`
 * ricevuto e si ricarica quando cambia. Gli importi arrivano `null` per chi non li può vedere: in quel caso
 * il grafico degli importi non compare affatto (mai uno zero finto).
 */

interface Loaded<T> {
  data: T | null
  loading: boolean
  error: string | null
}

function useLoaded<T>(load: () => Promise<T>, key: string): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ data: null, loading: true, error: null })
  useEffect(() => {
    let cancelled = false
    setState(s => ({ data: s.data, loading: true, error: null }))
    load()
      .then(data => { if (!cancelled) setState({ data, loading: false, error: null }) })
      .catch(e => { if (!cancelled) setState({ data: null, loading: false, error: e instanceof Error ? e.message : 'Errore nel caricamento' }) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return state
}

const dm = (s: string): string => {
  const [, m, d] = s.split('-')
  return `${d}/${m}`
}
const long = (s: string): string => new Date(s + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })

function periodLabels(points: Array<{ start: string; end: string }>, groupBy: SeriesGroupBy | undefined): { short: string[]; full: string[] } {
  const monthly = groupBy === 'month'
  return {
    short: points.map(p =>
      monthly
        ? new Date(p.start + 'T00:00:00').toLocaleDateString('it-IT', { month: 'short', year: '2-digit' })
        : dm(p.start)),
    full: points.map(p =>
      monthly
        ? new Date(p.start + 'T00:00:00').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
        : p.start === p.end ? long(p.start) : `${long(p.start)} - ${long(p.end)}`),
  }
}

const ERROR_TEXT = 'Non è stato possibile caricare il grafico.'

/** "Andamento": contratti e attivazioni (linee) e, se visibili, importi (barre). */
export function TrendChartCard({ range }: { range: ChartRange }) {
  const { data, loading, error } = useLoaded<StatsSeries>(() => fetchStatsSeries(range.from, range.to), `${range.from}|${range.to}`)
  const view = useMemo(() => {
    const pts = data?.points ?? []
    const labels = periodLabels(pts, data?.groupBy)
    const hasAmounts = pts.some(p => p.totalValue != null || p.expectedValue != null || p.collectedValue != null)
    const counts: ChartSeries[] = [
      { id: 'contracts', name: 'Contratti', values: pts.map(p => p.contracts), color: 'primary' },
      { id: 'activated', name: 'Attivazioni', values: pts.map(p => p.activated), color: 'tertiary' },
    ]
    const amounts: ChartSeries[] = [
      { id: 'expected', name: 'Fatturato presunto', values: pts.map(p => p.expectedValue), color: 'secondary' },
      { id: 'total', name: 'Fatturato', values: pts.map(p => p.totalValue), color: 'success' },
      { id: 'collected', name: 'Incassato', values: pts.map(p => p.collectedValue), color: 'warning' },
    ]
    return { labels, hasAmounts, counts, amounts }
  }, [data])

  const legend = [
    { label: 'Contratti', color: 'primary' as ChartColor },
    { label: 'Attivazioni', color: 'tertiary' as ChartColor },
    ...(view.hasAmounts
      ? [{ label: 'Fatturato presunto', color: 'secondary' as ChartColor }, { label: 'Fatturato', color: 'success' as ChartColor }, { label: 'Incassato', color: 'warning' as ChartColor }]
      : []),
  ]
  return (
    <ChartCard title="Andamento" subtitle="Contratti e attivazioni nel periodo" legend={legend}>
      {error ? (
        <Callout tone="error" role="alert">{ERROR_TEXT} {error}</Callout>
      ) : (
        <>
          <LineChart
            label="Andamento di contratti e attivazioni"
            categories={view.labels.short}
            categoryTitles={view.labels.full}
            series={view.counts}
            loading={loading && !data}
            emptyTitle="Nessun dato nel periodo"
          />
          {view.hasAmounts && (
            <BarChart
              label="Andamento degli importi"
              categories={view.labels.short}
              categoryTitles={view.labels.full}
              series={view.amounts}
              unit="eur"
              loading={loading && !data}
            />
          )}
        </>
      )}
    </ChartCard>
  )
}

/** "Per ramo": contratti e attivazioni per reparto, barre orizzontali. */
export function BranchChartCard({ range }: { range: ChartRange }) {
  const { data, loading, error } = useLoaded<{ branches: StatsBranch[] }>(() => fetchStatsByBranch(range.from, range.to), `${range.from}|${range.to}`)
  const branches = data?.branches ?? []
  return (
    <ChartCard
      title="Per ramo"
      subtitle="Contratti e attivazioni per reparto"
      legend={[{ label: 'Contratti', color: 'primary' }, { label: 'Attivazioni', color: 'tertiary' }]}
    >
      {error ? (
        <Callout tone="error" role="alert">{ERROR_TEXT} {error}</Callout>
      ) : (
        <BarChart
          horizontal
          label="Contratti e attivazioni per ramo"
          categories={branches.map(b => b.name)}
          categoryHeading="Ramo"
          series={[
            { id: 'contracts', name: 'Contratti', values: branches.map(b => b.contracts), color: 'primary' },
            { id: 'activated', name: 'Attivazioni', values: branches.map(b => b.activated), color: 'tertiary' },
          ]}
          height={Math.max(180, branches.length * 44 + 36)}
          loading={loading && !data}
          emptyTitle="Nessun contratto per ramo nel periodo"
        />
      )}
    </ChartCard>
  )
}

/** Lead per canale: ciambella dei lead e barre dei convertiti. */
export function LeadChannelChartCard({ range }: { range: ChartRange }) {
  const { data, loading, error } = useLoaded<{ channels: LeadChannelStat[] }>(() => fetchLeadsByChannel(range.from, range.to), `${range.from}|${range.to}`)
  const channels = data?.channels ?? []
  const slices: DonutSlice[] = channels.filter(c => c.leads > 0).map(c => ({ id: c.channel, label: c.label, value: c.leads }))
  return (
    <ChartCard title="Lead per canale" subtitle="Provenienza dei lead creati nel periodo">
      {error ? (
        <Callout tone="error" role="alert">{ERROR_TEXT} {error}</Callout>
      ) : (
        <>
          <DonutChart
            label="Lead per canale"
            slices={slices}
            totalLabel="lead"
            loading={loading && !data}
            emptyTitle="Nessun lead nel periodo"
          />
          {channels.length > 0 && (
            <BarChart
              horizontal
              label="Lead e convertiti per canale"
              categories={channels.map(c => c.label)}
              categoryHeading="Canale"
              series={[
                { id: 'leads', name: 'Lead', values: channels.map(c => c.leads), color: 'primary' },
                { id: 'converted', name: 'Convertiti', values: channels.map(c => c.converted), color: 'success' },
              ]}
              height={Math.max(180, channels.length * 44 + 36)}
            />
          )}
        </>
      )}
    </ChartCard>
  )
}

/** Provvigioni presunte / maturate / pagate per periodo (barre impilate). Nasconde tutto se gli importi sono `null`. */
export function CommissionsChartCard({ range, groupBy = 'month' }: { range: ChartRange; groupBy?: SeriesGroupBy | undefined }) {
  const { data, loading, error } = useLoaded<CommissionsSeries>(
    () => fetchCommissionsSeries(range.from, range.to, groupBy),
    `${range.from}|${range.to}|${groupBy}`,
  )
  const pts = data?.points ?? []
  if (data && pts.length > 0 && pts.every(p => p.presunte == null && p.maturate == null && p.pagate == null)) return null
  const labels = periodLabels(pts, data?.groupBy ?? groupBy)
  return (
    <ChartCard
      title="Provvigioni per mese"
      subtitle="Presunte, maturate e pagate"
      legend={[{ label: 'Presunte', color: 'secondary' }, { label: 'Maturate', color: 'primary' }, { label: 'Pagate', color: 'success' }]}
    >
      {error ? (
        <Callout tone="error" role="alert">{ERROR_TEXT} {error}</Callout>
      ) : (
        <BarChart
          stacked
          unit="eur"
          label="Provvigioni presunte, maturate e pagate"
          categories={labels.short}
          categoryTitles={labels.full}
          series={[
            { id: 'presunte', name: 'Presunte', values: pts.map(p => p.presunte), color: 'secondary' },
            { id: 'maturate', name: 'Maturate', values: pts.map(p => p.maturate), color: 'primary' },
            { id: 'pagate', name: 'Pagate', values: pts.map(p => p.pagate), color: 'success' },
          ]}
          loading={loading && !data}
          emptyTitle="Nessuna provvigione nel periodo"
        />
      )}
    </ChartCard>
  )
}
